import { useMemo, useState } from "react";
import {
  CalendarCheck,
  PlayCircle,
  PauseCircle,
  TrashSimple,
  ArrowsClockwise,
  Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  deleteTuitionPlan,
  planStatusLabel,
  runTuitionPlanNow,
  upsertTuitionPlan,
  WEEKDAY_LABELS,
} from "@/lib/tuitionPlans";
/**
 * Admin card embedded on /admin/billing/:familyId — creates / edits / pauses
 * / resumes / deletes a recurring tuition plan and triggers ad-hoc runs.
 * Supports monthly + weekly cadences (Module 42).
 */
export default function TuitionPlanCard({ billingProfileId, plan, onChange }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const initial = useMemo(
    () => ({
      cadence: plan?.cadence || "monthly",
      dayOfMonth: plan?.day_of_month || 1,
      dayOfWeek: Number.isInteger(plan?.day_of_week) ? plan.day_of_week : 1, // default Monday
      autoIssue: plan?.auto_issue ?? false,
      isActive: plan?.is_active ?? true,
      pauseUntil: plan?.pause_until || "",
      notes: plan?.notes || "",
    }),
    [plan],
  );
  const [form, setForm] = useState(initial);

  const save = async () => {
    setBusy(true);
    try {
      const isWeekly = form.cadence === "weekly";
      const saved = await upsertTuitionPlan({
        billingProfileId,
        cadence: form.cadence,
        dayOfMonth: isWeekly
          ? 1
          : Math.max(1, Math.min(28, parseInt(form.dayOfMonth, 10) || 1)),
        dayOfWeek: isWeekly
          ? Math.max(0, Math.min(6, parseInt(form.dayOfWeek, 10) || 0))
          : null,
        autoIssue: form.autoIssue,
        isActive: form.isActive,
        pauseUntil: form.pauseUntil || null,
        notes: form.notes?.trim() || null,
      });
      toast.success(plan ? "Plan updated" : "Plan created");
      setEditing(false);
      onChange?.(saved);
    } catch (err) {
      toast.error(err.message || "Could not save plan");
    } finally {
      setBusy(false);
    }
  };

  const pause = async () => {
    const until = window.prompt(
      "Pause until date (YYYY-MM-DD). Leave empty to pause indefinitely.",
      new Date(new Date().setMonth(new Date().getMonth() + 1))
        .toISOString()
        .slice(0, 10),
    );
    if (until === null) return;
    setBusy(true);
    try {
      const saved = await upsertTuitionPlan({
        billingProfileId,
        cadence: plan.cadence,
        dayOfMonth: plan.day_of_month,
        dayOfWeek: plan.day_of_week,
        autoIssue: plan.auto_issue,
        isActive: plan.is_active,
        pauseUntil: until || null,
        notes: plan.notes,
      });
      toast.success(until ? `Paused until ${until}` : "Paused indefinitely");
      onChange?.(saved);
    } catch (err) {
      toast.error(err.message || "Could not pause plan");
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    setBusy(true);
    try {
      const saved = await upsertTuitionPlan({
        billingProfileId,
        cadence: plan.cadence,
        dayOfMonth: plan.day_of_month,
        dayOfWeek: plan.day_of_week,
        autoIssue: plan.auto_issue,
        isActive: true,
        pauseUntil: null,
        notes: plan.notes,
      });
      toast.success("Plan resumed");
      onChange?.(saved);
    } catch (err) {
      toast.error(err.message || "Could not resume");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this plan? Existing invoices stay untouched.")) return;
    setBusy(true);
    try {
      await deleteTuitionPlan(plan.id);
      toast.success("Plan deleted");
      onChange?.(null);
    } catch (err) {
      toast.error(err.message || "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm("Run this plan now? Duplicate protections still apply.")) return;
    setBusy(true);
    try {
      const rows = await runTuitionPlanNow(plan.id);
      const row = Array.isArray(rows) ? rows[0] : rows;
      setRunResult(row || { status: "not_due" });
      if (!row) {
        toast(
          `Nothing due yet — next run on ${
            plan.next_run_on
              ? new Date(plan.next_run_on).toLocaleDateString()
              : "—"
          }`,
        );
      } else if (row.status === "created") toast.success("Invoice created");
      else if (row.status === "voided_and_regenerated") toast.success("Regenerated");
      else if (row.reason) toast(`Skipped · ${row.reason}`);
      else toast(`Status: ${row.status || "unknown"}`);
      onChange?.();
    } catch (err) {
      toast.error(err.message || "Run failed");
    } finally {
      setBusy(false);
    }
  };

  // ------------------------------------------------------------- rendering
  const header = (
    <div className="mb-3 flex items-center gap-2">
      <CalendarCheck
        size={18}
        weight="duotone"
        className="text-primary-hover"
      />
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        Recurring tuition plan
      </p>
      {plan && (
        <span
          data-testid="tuition-plan-status"
          className={`ml-auto rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
            plan.is_active && !plan.pause_until
              ? "border-primary/30 bg-primary/10 text-primary-hover"
              : "border-neutral-200 bg-neutral-100 text-neutral-600"
          }`}
        >
          {planStatusLabel(plan)}
        </span>
      )}
    </div>
  );

  if (editing) {
    return (
      <section
        data-testid="tuition-plan-card"
        className="rounded-2xl border border-neutral-200 bg-white p-5"
      >
        {header}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Cadence
            </span>
            <select
              data-testid="tuition-plan-cadence"
              value={form.cadence}
              onChange={(e) => setForm({ ...form, cadence: e.target.value })}
              className={inputClass}
            >
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
            </select>
          </label>
          {form.cadence === "weekly" ? (
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                Day of week
              </span>
              <select
                data-testid="tuition-plan-day-of-week"
                value={form.dayOfWeek}
                onChange={(e) =>
                  setForm({ ...form, dayOfWeek: parseInt(e.target.value, 10) })
                }
                className={inputClass}
              >
                {WEEKDAY_LABELS.map((label, idx) => (
                  <option key={idx} value={idx}>{label}</option>
                ))}
              </select>
            </label>
          ) : (
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                Day of month (1–28)
              </span>
              <input
                data-testid="tuition-plan-day"
                type="number"
                min="1"
                max="28"
                value={form.dayOfMonth}
                onChange={(e) => setForm({ ...form, dayOfMonth: e.target.value })}
                className={inputClass}
              />
            </label>
          )}
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Pause until (optional)
            </span>
            <input
              data-testid="tuition-plan-pause"
              type="date"
              value={form.pauseUntil}
              onChange={(e) => setForm({ ...form, pauseUntil: e.target.value })}
              className={inputClass}
            />
          </label>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex items-start gap-2 rounded-xl border border-neutral-200 bg-neutral-50 p-3">
            <input
              type="checkbox"
              data-testid="tuition-plan-auto-issue"
              checked={form.autoIssue}
              onChange={(e) => setForm({ ...form, autoIssue: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[#5A7E46]"
            />
            <span className="text-sm text-neutral-800">
              <span className="font-semibold">Auto-issue to parent</span>{" "}
              <span className="block text-xs text-neutral-500">
                Issues the draft immediately after generation instead of waiting
                for admin review.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 rounded-xl border border-neutral-200 bg-neutral-50 p-3">
            <input
              type="checkbox"
              data-testid="tuition-plan-active"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[#5A7E46]"
            />
            <span className="text-sm text-neutral-800">
              <span className="font-semibold">Active</span>{" "}
              <span className="block text-xs text-neutral-500">
                When off, scheduler skips this plan entirely.
              </span>
            </span>
          </label>
        </div>
        <textarea
          data-testid="tuition-plan-notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Internal notes (optional)"
          className={`${inputClass} mt-3 min-h-[56px]`}
        />
        <div className="mt-3 flex justify-end gap-2">
          {plan && (
            <button
              type="button"
              onClick={() => {
                setForm(initial);
                setEditing(false);
              }}
              className="rounded-full px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
              data-testid="tuition-plan-cancel"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="tuition-plan-save"
            className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            {busy ? "Saving…" : plan ? "Save plan" : "Create plan"}
          </button>
        </div>
      </section>
    );
  }

  // Compact read-mode
  const paused = plan?.pause_until && new Date(plan.pause_until) > new Date();

  return (
    <section
      data-testid="tuition-plan-card"
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      {header}
      {plan ? (
        <>
          <dl className="grid grid-cols-2 gap-y-2 text-sm sm:grid-cols-4">
            <PlanField
              label="Cadence"
              value={plan.cadence === "weekly" ? "Weekly" : "Monthly"}
              testId="tuition-plan-cadence-display"
            />
            <PlanField
              label={plan.cadence === "weekly" ? "Billing day" : "Billing day"}
              value={
                plan.cadence === "weekly"
                  ? WEEKDAY_LABELS[plan.day_of_week] || "—"
                  : plan.day_of_month
              }
              testId="tuition-plan-billing-day"
            />
            <PlanField
              label="Next run"
              value={plan.next_run_on ? new Date(plan.next_run_on).toLocaleDateString() : "—"}
              testId="tuition-plan-next-run"
            />
            <PlanField
              label="Last run"
              value={plan.last_run_at ? new Date(plan.last_run_at).toLocaleString() : "Never"}
              testId="tuition-plan-last-run"
            />
          </dl>
          {plan.last_result && (
            <p
              data-testid="tuition-plan-last-result"
              className="mt-3 rounded-xl bg-neutral-50 px-3 py-2 text-xs text-neutral-600"
            >
              Last result: {(plan.last_result.status || "unknown")}{" "}
              {plan.last_result.reason ? `· ${plan.last_result.reason}` : ""}{" "}
              {plan.last_result.amount_cents
                ? `· ${(plan.last_result.amount_cents / 100).toFixed(2)}`
                : ""}
              {plan.last_result.issued ? " · issued" : ""}
            </p>
          )}
          {runResult && (
            <p
              data-testid="tuition-plan-run-result"
              className="mt-3 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-neutral-700"
            >
              {runResult.status === "not_due" ? (
                <>
                  Run just now: <span className="font-semibold">nothing due</span>
                  {plan.next_run_on
                    ? ` · next run on ${new Date(plan.next_run_on).toLocaleDateString()}`
                    : ""}
                </>
              ) : (
                <>
                  Run just now: <span className="font-semibold">{runResult.status}</span>
                  {runResult.reason ? ` · ${runResult.reason}` : ""}
                  {runResult.invoice_id ? ` · invoice created` : ""}
                  {runResult.issued ? ` · issued` : ""}
                </>
              )}
            </p>
          )}
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={runNow}
              disabled={busy}
              data-testid="tuition-plan-run-now"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
            >
              <Lightning size={12} weight="bold" />
              Run now
            </button>
            {paused ? (
              <button
                type="button"
                onClick={resume}
                disabled={busy}
                data-testid="tuition-plan-resume"
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/5"
              >
                <PlayCircle size={12} weight="bold" />
                Resume
              </button>
            ) : (
              <button
                type="button"
                onClick={pause}
                disabled={busy || !plan.is_active}
                data-testid="tuition-plan-pause-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:border-neutral-300"
              >
                <PauseCircle size={12} weight="bold" />
                Pause
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditing(true)}
              disabled={busy}
              data-testid="tuition-plan-edit"
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:border-neutral-300"
            >
              <ArrowsClockwise size={12} weight="bold" />
              Edit
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              aria-label="Delete plan"
              data-testid="tuition-plan-delete"
              className="rounded-full p-2 text-neutral-400 hover:bg-[#C6851E]/10 hover:text-[#C6851E] disabled:opacity-40"
            >
              <TrashSimple size={14} weight="bold" />
            </button>
          </div>
        </>
      ) : (
        <div data-testid="tuition-plan-empty" className="flex flex-col items-start gap-3">
          <p className="text-sm text-neutral-600">
            No recurring tuition plan for this family yet. Create one and the
            scheduler will generate invoices on the day you pick — with the
            same duplicate protection + sibling-discount logic as the bulk tool.
          </p>
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="tuition-plan-create"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
          >
            Create plan
          </button>
        </div>
      )}
    </section>
  );
}

function PlanField({ label, value, testId }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
      </dt>
      <dd
        data-testid={testId}
        className="mt-0.5 font-semibold text-neutral-900"
      >
        {value}
      </dd>
    </div>
  );
}

const inputClass =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";
